import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, 
  Shield, 
  FileText, 
  CreditCard, 
  User, 
  XCircle,
  AlertTriangle,
  HelpCircle,
  Phone,
  Mail,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  ExternalLink,
  Clock,
  CheckCircle,
  Info,
  DollarSign,
  Car,
  FileCheck,
  Lock,
  RefreshCw,
  MessageSquare,
  TrendingDown,
  Calculator,
  Calendar,
  AlertCircle,
  BookOpen,
  Zap,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import PortalLayout from '../components/PortalLayout';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// FAQ Data organized by category
const faqData = {
  coverage: {
    title: 'Coverage & Policy Basics',
    icon: Shield,
    color: 'blue',
    description: 'Understand your diminished value protection',
    questions: [
      {
        id: 'what-does-losspay-cover',
        question: 'What does LossPay cover?',
        answer: `LossPay provides **Diminished Value Protection** — coverage for the loss in your vehicle's market value after it has been damaged in an accident and repaired.

**Key Points:**
- We cover the difference between your vehicle's pre-accident value and its post-repair value
- Coverage applies even when repairs are done correctly and completely
- Protection up to your selected coverage limit ($10,000 - $50,000)

**Important:** Standard auto insurance typically does NOT cover diminished value. That's where LossPay comes in.`,
        keywords: ['cover', 'coverage', 'protection', 'what', 'diminished value'],
        cta: { label: 'View Coverage Details', path: '/coverage' }
      },
      {
        id: 'what-is-diminished-value',
        question: 'What is diminished value?',
        answer: `**Diminished value** is the reduction in your vehicle's market worth that occurs simply because it has been in an accident — even after full repairs.

**Why It Matters:**
- A repaired vehicle is worth less than an identical vehicle with no accident history
- CarFax and other history reports permanently show the accident
- Buyers pay less for vehicles with accident history
- This loss is typically 10-25% of your vehicle's value

**Example:** If your $40,000 vehicle loses 15% of its value after an accident, that's a $6,000 loss in equity — money you'd never recover without diminished value coverage.`,
        keywords: ['diminished', 'value', 'worth', 'equity', 'loss']
      },
      {
        id: 'what-is-not-covered',
        question: 'What is NOT covered?',
        answer: `LossPay does NOT cover:

**Exclusions:**
- **Total Loss:** If your vehicle is declared a total loss, diminished value coverage does not apply
- **Pre-existing Damage:** Damage that existed before your policy started
- **Mechanical Failures:** Breakdowns not caused by a covered accident
- **Wear and Tear:** Normal depreciation from age and use
- **Intentional Damage:** Self-inflicted or fraudulent claims
- **Commercial Vehicles:** Vehicles used primarily for business purposes (unless specified)
- **Cosmetic-Only Damage:** Minor scratches or dings that don't affect structure

**Note:** Coverage is subject to the full terms and conditions in your policy documents.`,
        keywords: ['not covered', 'exclusion', 'exclude', 'total loss', 'won\'t cover'],
        cta: { label: 'View Documents', path: '/portal' }
      },
      {
        id: 'trigger-loss',
        question: 'What qualifies as a "trigger loss"?',
        answer: `A **trigger loss** is the qualifying event that activates your diminished value coverage.

**Requirements:**
1. Your vehicle must be involved in a covered accident
2. The damage must be repaired (not totaled)
3. The accident must be reported to your primary auto insurer
4. You must file your LossPay claim within 21 days of repair completion

**Examples of Trigger Losses:**
- Collision with another vehicle
- Single-car accidents (hitting a guardrail, tree, etc.)
- Hit-and-run incidents
- Weather-related accidents (hail, flood damage that's repairable)

**Not Trigger Losses:**
- Mechanical failure
- Vandalism (minor, non-structural)
- Theft without collision damage`,
        keywords: ['trigger', 'loss', 'qualify', 'event', 'accident']
      },
      {
        id: 'need-primary-insurance',
        question: 'Do I need primary insurance?',
        answer: `**Yes.** LossPay is supplemental coverage that works alongside your primary auto insurance.

**Requirements:**
- You must maintain active, valid auto insurance on your covered vehicle
- Your primary insurer handles the vehicle repair
- LossPay covers the diminished value AFTER repairs are complete

**Why This Matters:**
- Primary insurance pays for repairs
- LossPay pays for the value your car loses despite those repairs
- Both coverages work together to protect you fully

**If your primary insurance lapses**, your LossPay coverage may also be affected. Always maintain continuous coverage.`,
        keywords: ['primary', 'insurance', 'need', 'require', 'other insurance']
      },
      {
        id: 'coverage-limits',
        question: 'What are coverage limits and deductibles?',
        answer: `**Coverage Limits:**
Your policy has a maximum payout amount you selected at purchase:
- Standard: $10,000
- Enhanced: $25,000  
- Premium: $50,000

**Deductible:**
Your deductible is the amount you pay before coverage kicks in:
- Typical range: $250 - $1,000
- Lower deductible = higher premium
- Deductible is subtracted from your claim payout

**Example:**
If your diminished value is $8,000 and you have a $500 deductible with $25,000 coverage:
- You receive: $8,000 - $500 = **$7,500**`,
        keywords: ['limit', 'deductible', 'maximum', 'payout', 'coverage amount'],
        cta: { label: 'View Your Coverage', path: '/coverage' }
      },
      {
        id: 'coverage-period',
        question: 'When does coverage start and end?',
        answer: `**Coverage Start:**
Your coverage begins on the **effective date** shown on your policy documents — typically the date of purchase or a future date you selected.

**Coverage End:**
Your coverage ends on the **expiration date** (usually 12 months from start) OR when:
- You cancel the policy
- The policy is not renewed
- The covered vehicle is sold or totaled
- You fail to maintain primary insurance

**Renewal:**
Policies auto-renew unless you cancel. You'll receive a renewal notice 30 days before expiration.`,
        keywords: ['start', 'end', 'begin', 'expire', 'period', 'renewal'],
        cta: { label: 'Check Policy Dates', path: '/coverage' }
      }
    ]
  },
  claims: {
    title: 'Claims & Payouts',
    icon: FileText,
    color: 'green',
    description: 'File claims and understand the payout process',
    questions: [
      {
        id: 'how-to-file-claim',
        question: 'How do I file a claim?',
        answer: `**Filing a claim is simple:**

**Step 1: Gather Your Documents**
- Primary insurance claim number
- Repair estimate or invoice
- Photos of damage (before repair)
- Police report (if applicable)

**Step 2: Start Your Claim**
- Log into your LossPay account
- Click "File a Claim" on your dashboard
- Complete the claim form with accident details
- Upload required documents

**Step 3: Submit & Track**
- Review your information
- Submit your claim
- Track status in your Claims Center

**Deadline:** Claims must be filed within **21 days** of repair completion.`,
        keywords: ['file', 'claim', 'submit', 'start', 'how to'],
        cta: { label: 'File a Claim Now', path: '/claims/file' },
        priority: true
      },
      {
        id: 'required-documents',
        question: 'What documents are required for a claim?',
        answer: `**Required Documents:**

1. **Primary Insurance Claim Number**
   - The claim number from your auto insurance company

2. **Repair Documentation**
   - Final repair invoice showing work completed
   - Itemized list of repairs performed

3. **Damage Photos**
   - Pre-repair photos showing the damage
   - Post-repair photos (if available)

4. **Police Report** (if applicable)
   - Required for accidents involving other parties
   - Helpful for hit-and-run incidents

5. **Vehicle Information**
   - Current mileage
   - VIN confirmation

**Pro Tip:** The more documentation you provide, the faster your claim processes.`,
        keywords: ['document', 'required', 'need', 'paperwork', 'upload'],
        cta: { label: 'Start Your Claim', path: '/claims/file' }
      },
      {
        id: 'claim-deadline',
        question: 'What is the deadline to file a claim?',
        answer: `**Important: You have 21 days to file your claim.**

The clock starts when your vehicle repairs are **completed** — not when the accident occurred.

**Timeline:**
- Day 0: Repairs completed
- Day 1-21: File your LossPay claim
- Day 22+: Claim may be denied

**Why the Deadline?**
- Ensures accurate diminished value assessment
- Prevents fraud
- Allows timely processing

**What If I Miss It?**
Late claims are reviewed on a case-by-case basis but may be denied. If you have extenuating circumstances, contact support immediately.`,
        keywords: ['deadline', 'time', 'limit', 'days', 'late', 'when'],
        priority: true
      },
      {
        id: 'how-dv-calculated',
        question: 'How is diminished value calculated?',
        answer: `**Our Diminished Value Calculation:**

We use an industry-standard formula that considers:

1. **Pre-Accident Value**
   - Your vehicle's fair market value before damage
   - Based on make, model, year, mileage, condition

2. **Severity of Damage**
   - Structural vs. cosmetic damage
   - Number of damaged areas
   - Type of repairs needed

3. **Repair Quality**
   - OEM vs. aftermarket parts
   - Certified repair facility

4. **Market Factors**
   - Regional pricing differences
   - Vehicle demand
   - Comparable sales data

**Typical Range:** Most diminished value claims fall between 10-25% of the vehicle's pre-accident value.

**Example:**
- Vehicle Value: $35,000
- Moderate structural damage
- Estimated DV: 15% = $5,250`,
        keywords: ['calculate', 'how much', 'value', 'formula', 'determine']
      },
      {
        id: 'claim-timeline',
        question: 'How long does claim review take?',
        answer: `**Typical Claim Timeline:**

| Stage | Timeframe |
|-------|-----------|
| Claim Submitted | Day 0 |
| Initial Review | 1-3 business days |
| Document Verification | 3-5 business days |
| Valuation Assessment | 5-7 business days |
| Decision & Payout | 7-14 business days |

**Total: 7-14 business days** for most claims

**What Can Delay Processing:**
- Missing or unclear documents
- Need for additional information
- Complex damage assessment
- High claim volume periods

**Speed It Up:**
- Submit all documents upfront
- Respond quickly to requests
- Ensure photos are clear and complete`,
        keywords: ['timeline', 'how long', 'wait', 'process', 'time'],
        cta: { label: 'Track Your Claim', path: '/claims' }
      },
      {
        id: 'after-claim-submitted',
        question: 'What happens after I submit a claim?',
        answer: `**After You Submit:**

**1. Confirmation (Immediate)**
- You'll receive email confirmation
- Claim number assigned
- Can track in Claims Center

**2. Initial Review (1-3 days)**
- Our team reviews your submission
- Checks for completeness
- May request additional info

**3. Valuation (3-7 days)**
- Diminished value assessed
- Vehicle history analyzed
- Market comparisons made

**4. Decision (7-14 days)**
- Claim approved or denied
- Payout amount determined
- You'll be notified via email

**5. Payment (if approved)**
- Direct deposit or check
- Typically within 5 business days of approval

**Stay Updated:** Check your Claims Center for real-time status updates.`,
        keywords: ['after', 'submit', 'next', 'happen', 'status'],
        cta: { label: 'View Claims Center', path: '/claims' }
      },
      {
        id: 'appeal-claim',
        question: 'Can I appeal a claim decision?',
        answer: `**Yes, you can appeal.**

**If Your Claim Was Denied:**

1. **Review the Denial Reason**
   - Check your email and Claims Center
   - Understand why it was denied

2. **Gather Additional Evidence**
   - New documents
   - Clarifying information
   - Expert opinions

3. **Submit an Appeal**
   - Contact support within 30 days
   - Provide your claim number
   - Explain why you disagree
   - Include new evidence

**Common Denial Reasons:**
- Filed after 21-day deadline
- Total loss (not covered)
- Missing documentation
- Pre-existing damage

**Appeal Success Tips:**
- Be specific about your disagreement
- Provide clear documentation
- Respond promptly to requests`,
        keywords: ['appeal', 'denied', 'reject', 'disagree', 'dispute']
      }
    ]
  },
  billing: {
    title: 'Billing & Payments',
    icon: CreditCard,
    color: 'purple',
    description: 'Manage payments and understand billing',
    questions: [
      {
        id: 'how-billing-works',
        question: 'How does billing work?',
        answer: `**Payment Options:**

**Pay in Full**
- One-time payment at purchase
- No additional fees
- Coverage active immediately

**Monthly Payments**
- Spread cost over 12 months
- Small processing fee may apply
- Auto-charged each month

**Your billing date** is the same day each month as your original purchase date.

**Example:** If you purchased on the 15th, you'll be billed on the 15th each month.`,
        keywords: ['billing', 'payment', 'monthly', 'pay', 'charge'],
        cta: { label: 'View Billing Details', path: '/settings?tab=billing' }
      },
      {
        id: 'when-charged',
        question: 'When are payments charged?',
        answer: `**Payment Schedule:**

**Monthly Plans:**
- Charged on your billing date each month
- Typically same day you enrolled
- Processed in the morning (EST)

**Annual Plans:**
- Single charge at purchase
- Renewal charge 12 months later

**Renewal Payments:**
- Charged automatically unless cancelled
- 30-day advance notice provided
- Same payment method on file

**Failed Payments:**
If a payment fails, we'll retry within 3 days and notify you via email.`,
        keywords: ['when', 'charge', 'date', 'schedule', 'automatic']
      },
      {
        id: 'failed-payment',
        question: 'What happens if my payment fails?',
        answer: `**If Your Payment Fails:**

**Immediate:**
- You'll receive an email notification
- Coverage remains active (grace period)

**Within 3 Days:**
- We automatically retry the payment
- Check that your card info is current

**After 3 Days:**
- Second retry attempted
- Additional notification sent

**After 7 Days:**
- Final notice sent
- Coverage at risk of cancellation

**After 14 Days:**
- Policy may be cancelled
- Re-enrollment required

**Fix It Now:**
Update your payment method in Settings > Billing to avoid any interruption.`,
        keywords: ['failed', 'decline', 'reject', 'not working', 'error'],
        cta: { label: 'Update Payment Method', path: '/settings?tab=billing' },
        priority: true
      },
      {
        id: 'refund-policy',
        question: 'What is your refund policy?',
        answer: `**Refund Eligibility:**

**Full Refund (within 30 days):**
- Cancel within 30 days of purchase
- No claims filed
- 100% premium refunded

**Pro-Rated Refund (after 30 days):**
- Cancel anytime after 30 days
- Refund based on unused coverage period
- Less any administrative fees
- No refund if claim was paid

**No Refund:**
- If a claim has been paid
- Less than 30 days remaining on policy
- Policy cancelled due to fraud

**Processing Time:**
Refunds typically process within 5-10 business days to your original payment method.`,
        keywords: ['refund', 'money back', 'cancel', 'return'],
        cta: { label: 'View Cancellation Policy', path: '/settings?tab=policy' }
      },
      {
        id: 'update-payment',
        question: 'How do I update my payment method?',
        answer: `**Update Your Payment Method:**

**Step 1:** Log into your account

**Step 2:** Go to Settings > Billing

**Step 3:** Click "Update Payment Method"

**Step 4:** Enter new card details

**Step 5:** Save changes

**Accepted Payment Methods:**
- Visa
- Mastercard
- American Express
- Discover
- Debit cards

**Note:** Your next payment will use the new method. Previous transactions won't be affected.`,
        keywords: ['update', 'change', 'card', 'payment method', 'new card'],
        cta: { label: 'Update Payment Now', path: '/settings?tab=billing' }
      },
      {
        id: 'auto-renewal',
        question: 'How does auto-renewal work?',
        answer: `**Auto-Renewal Policy:**

Your LossPay policy **automatically renews** to ensure continuous protection.

**30 Days Before Expiration:**
- Renewal notice sent via email
- New premium amount shown
- Option to cancel or modify

**At Renewal:**
- Payment charged to card on file
- New policy period begins
- Same coverage continues

**To Cancel Auto-Renewal:**
- Go to Settings > Policy
- Click "Cancel Auto-Renewal"
- Coverage ends at expiration date

**Why Auto-Renewal?**
- No gap in coverage
- Continuous protection
- One less thing to remember`,
        keywords: ['auto', 'renewal', 'renew', 'automatic', 'expire']
      }
    ]
  },
  account: {
    title: 'Account & Security',
    icon: User,
    color: 'orange',
    description: 'Manage your account and stay secure',
    questions: [
      {
        id: 'create-account',
        question: 'How do I create or access my account?',
        answer: `**New Customers:**
Your account is created when you purchase a policy. You'll receive:
- Welcome email with login instructions
- Temporary password (or link to set one)
- Access to your Policyholder Portal

**Existing Customers:**
1. Go to secure.losspay.com
2. Click "Sign In"
3. Enter your email and password
4. Access your dashboard

**First Time Logging In?**
- Check your email for setup instructions
- Click the link to create your password
- Complete any required profile info`,
        keywords: ['create', 'account', 'login', 'access', 'sign in', 'new'],
        cta: { label: 'Go to Dashboard', path: '/portal' }
      },
      {
        id: 'reset-password',
        question: 'How do I reset my password?',
        answer: `**Reset Your Password:**

**Step 1:** Go to the Sign In page

**Step 2:** Click "Forgot Password?"

**Step 3:** Enter your email address

**Step 4:** Check your email for reset link

**Step 5:** Click the link and create new password

**Password Requirements:**
- At least 8 characters
- One uppercase letter
- One lowercase letter
- One number
- One special character

**Didn't receive the email?**
- Check spam/junk folder
- Verify you're using the correct email
- Contact support if issues persist`,
        keywords: ['reset', 'password', 'forgot', 'change password', 'can\'t login']
      },
      {
        id: 'update-info',
        question: 'How do I update my personal information?',
        answer: `**Update Your Information:**

**In Your Account:**
Go to Settings > Profile to update:
- Name
- Email address
- Phone number
- Mailing address

**Changes That Require Support:**
Contact us to change:
- Covered vehicle
- Policy type
- Coverage limits

**Important Notes:**
- Email changes require verification
- Address changes may affect coverage
- Keep info current for claims processing`,
        keywords: ['update', 'change', 'personal', 'information', 'profile', 'address'],
        cta: { label: 'Update Profile', path: '/settings?tab=profile' }
      },
      {
        id: 'security-tips',
        question: 'What security best practices should I follow?',
        answer: `**Keep Your Account Secure:**

**Strong Password:**
- Use unique password for LossPay
- Don't reuse passwords from other sites
- Consider a password manager

**Protect Your Login:**
- Never share your password
- Log out on shared devices
- Watch for phishing emails

**Verify Communications:**
- LossPay emails come from @losspay.com
- We never ask for full card numbers via email
- When in doubt, log in directly

**Monitor Your Account:**
- Review account activity regularly
- Report suspicious activity immediately
- Keep contact info current`,
        keywords: ['security', 'safe', 'protect', 'secure', 'hack']
      }
    ]
  },
  cancellation: {
    title: 'Policy Changes & Cancellation',
    icon: XCircle,
    color: 'red',
    description: 'Modify or cancel your policy',
    questions: [
      {
        id: 'how-to-cancel',
        question: 'How do I cancel my policy?',
        answer: `**To Cancel Your Policy:**

**Option 1: Online**
1. Go to Settings > Policy
2. Click "Cancel Policy"
3. Select cancellation reason
4. Confirm cancellation

**Option 2: Contact Support**
- Email: support@losspay.com
- Phone: Available in business hours
- Live chat: On our website

**Before You Cancel:**
- Review refund eligibility
- Consider if you have upcoming travel/needs
- Remember: re-enrollment may cost more later`,
        keywords: ['cancel', 'stop', 'end', 'terminate', 'quit'],
        cta: { label: 'View Cancellation Options', path: '/settings?tab=policy' }
      },
      {
        id: 'cancellation-effective',
        question: 'When does cancellation become effective?',
        answer: `**Cancellation Timing:**

**Standard Cancellation:**
- Effective **30 days** from request date
- Coverage continues during this period
- You can still file claims during the 30 days

**Why 30 Days?**
- Allows time to find alternative coverage
- Ensures no coverage gaps
- Industry standard practice

**Immediate Cancellation:**
Only available in limited circumstances:
- Within 30-day money-back period
- Vehicle sold or totaled
- Duplicate coverage discovered

**Your cancellation confirmation will show the exact effective date.**`,
        keywords: ['when', 'effective', 'start', 'date', '30 days']
      },
      {
        id: 'refund-eligibility',
        question: 'Am I eligible for a refund if I cancel?',
        answer: `**Refund Eligibility:**

| Situation | Refund |
|-----------|--------|
| Cancel within 30 days, no claims | Full refund |
| Cancel after 30 days, no claims | Pro-rated refund |
| Cancel with paid claim | No refund |
| Less than 30 days left | No refund |

**Pro-Rated Calculation:**
Unused premium minus administrative fee ($25)

**Example:**
- Annual premium: $299
- Cancelled at 6 months
- Unused: $149.50
- Fee: $25
- **Refund: $124.50**

**Processing:** 5-10 business days to original payment method`,
        keywords: ['refund', 'eligible', 'money', 'back', 'cancel refund']
      },
      {
        id: 'after-cancellation',
        question: 'What happens after I cancel?',
        answer: `**After Cancellation:**

**Immediately:**
- Confirmation email sent
- Cancellation date confirmed
- Coverage continues until effective date

**On Effective Date:**
- Coverage ends at 11:59 PM
- No new claims accepted after this
- Access to view documents remains

**Refund Processing:**
- If eligible, processed within 5-10 days
- Credited to original payment method

**Future Access:**
- Portal access for 90 days (view only)
- Documents available for download
- Can re-enroll anytime

**Re-Enrollment:**
You can purchase a new policy anytime, but premiums may differ.`,
        keywords: ['after', 'cancel', 'then', 'next', 'happen']
      },
      {
        id: 're-enrollment',
        question: 'Can I re-enroll after cancelling?',
        answer: `**Re-Enrollment:**

**Yes, you can re-enroll at any time.**

**What to Know:**
- New application required
- New effective date
- Premium may be different
- Vehicle re-verification needed

**Waiting Period:**
No mandatory waiting period, but:
- New coverage starts fresh
- No retroactive coverage
- Previous claims don't carry over

**Pro Tip:**
If you're unsure about cancelling, consider keeping coverage active. Gaps in coverage mean gaps in protection.`,
        keywords: ['re-enroll', 'again', 'new policy', 'come back', 'rejoin']
      }
    ]
  },
  scenarios: {
    title: 'Claims Scenarios',
    icon: Car,
    color: 'teal',
    description: 'Real examples of how coverage works',
    questions: [
      {
        id: 'minor-vs-major',
        question: 'Minor accident vs. major accident — what\'s the difference?',
        answer: `**Minor Accident:**
- Fender bender, parking lot damage
- Cosmetic repairs only
- No structural damage
- **Diminished value: 5-10%**

**Example:** $30,000 car with bumper damage
- Repair cost: $1,500
- Diminished value: ~$2,000
- LossPay payout: $2,000 - deductible

---

**Major Accident:**
- Significant collision
- Structural/frame damage
- Airbag deployment
- **Diminished value: 15-25%**

**Example:** $40,000 car with frame damage
- Repair cost: $12,000
- Diminished value: ~$8,000
- LossPay payout: $8,000 - deductible

**Key Point:** Even "minor" accidents create permanent value loss that CarFax will show forever.`,
        keywords: ['minor', 'major', 'accident', 'example', 'difference']
      },
      {
        id: 'when-dv-applies',
        question: 'When does diminished value coverage apply?',
        answer: `**Coverage APPLIES When:**

✅ Your vehicle is damaged in an accident
✅ The vehicle is repaired (not totaled)
✅ You have active LossPay coverage
✅ You file within 21 days of repair
✅ The damage affects vehicle value

**Real Scenarios Where It Applies:**

**Scenario 1: Rear-ended at stoplight**
- Other driver's fault
- Your car repaired perfectly
- CarFax now shows accident
- ✅ **Diminished value covered**

**Scenario 2: Hit a deer**
- Comprehensive claim
- Front-end repaired
- Structural damage noted
- ✅ **Diminished value covered**

**Scenario 3: Hail damage**
- Multiple dents repaired
- Paint work done
- No structural issues
- ✅ **Diminished value covered** (if significant)`,
        keywords: ['apply', 'applies', 'when', 'qualify', 'eligible']
      },
      {
        id: 'not-eligible-scenarios',
        question: 'When is a claim NOT eligible?',
        answer: `**Coverage Does NOT Apply When:**

❌ **Total Loss**
Vehicle is declared a total loss — diminished value doesn't apply because you're being compensated for full value.

❌ **Pre-Existing Damage**
Damage existed before your policy started.

❌ **No Repair Needed**
Scratch or ding so minor it doesn't require repair or affect value.

❌ **Filed Too Late**
Claim submitted after 21-day deadline.

❌ **Mechanical Only**
Engine failure, transmission issues — not accident damage.

❌ **Fraud**
Intentional damage or misrepresentation.

**Real Scenario:**
Car totaled after major collision → Insurance pays actual cash value → No diminished value claim (vehicle wasn't repaired).`,
        keywords: ['not eligible', 'denied', 'won\'t', 'can\'t', 'reject']
      },
      {
        id: 'total-loss-explained',
        question: 'Total loss vs. repairable — what happens?',
        answer: `**Total Loss:**
When repair costs exceed ~70-80% of vehicle value.

**What Happens:**
- Primary insurance pays Actual Cash Value (ACV)
- Vehicle is typically sold to salvage
- **No diminished value claim** — vehicle wasn't repaired

---

**Repairable:**
When it makes financial sense to fix the vehicle.

**What Happens:**
- Primary insurance pays for repairs
- Vehicle is fixed and returned to you
- **Diminished value claim eligible** — you can file with LossPay

---

**The Gray Area:**
Sometimes a vehicle is borderline. If your insurer declares it repairable:
- Get it fixed
- File your LossPay claim
- Recover your diminished value

**Pro Tip:** If your vehicle is close to total loss threshold, the diminished value after repair could be significant (20-30%).`,
        keywords: ['total loss', 'totaled', 'repairable', 'repair', 'salvage']
      }
    ]
  },
  support: {
    title: 'Support & Contact',
    icon: MessageSquare,
    color: 'indigo',
    description: 'Get help when you need it',
    questions: [
      {
        id: 'when-contact',
        question: 'When should I contact support?',
        answer: `**Contact Support When:**

**Claims Issues:**
- Question about claim status
- Missing claim documents
- Appeal a claim decision
- Urgent claim situation

**Account Issues:**
- Can't log in
- Account locked
- Suspicious activity
- Need to verify identity

**Billing Issues:**
- Payment not processing
- Unexpected charge
- Refund questions
- Update payment issues

**Policy Questions:**
- Coverage clarification
- Policy changes needed
- Cancellation help
- Document requests

**Try Self-Service First:**
Many answers are in this Help Center or your account dashboard!`,
        keywords: ['contact', 'support', 'help', 'call', 'email', 'reach']
      },
      {
        id: 'response-times',
        question: 'What are your response times?',
        answer: `**Expected Response Times:**

| Channel | Response Time |
|---------|---------------|
| Email | Within 24 hours |
| Contact Form | Within 24 hours |
| Phone | Immediate (business hours) |

**Business Hours:**
Monday - Friday: 9 AM - 6 PM EST
Saturday: 10 AM - 2 PM EST
Sunday: Closed

**Urgent Matters:**
For time-sensitive claim issues, call during business hours for fastest response.

**Holiday Hours:**
Reduced hours on major holidays. Check our website for updates.`,
        keywords: ['response', 'time', 'wait', 'how long', 'hours']
      },
      {
        id: 'escalation',
        question: 'How do I escalate an issue?',
        answer: `**Escalation Process:**

**Level 1: Standard Support**
- Email or contact form
- Most issues resolved here
- 24-hour response

**Level 2: Senior Support**
- If not resolved in 48 hours
- Request escalation in your reply
- Supervisor review

**Level 3: Management Review**
- Complex or unresolved issues
- Formal complaint filed
- 5-business day response

**To Escalate:**
1. Reference your ticket/case number
2. Explain why initial response didn't help
3. State what resolution you're seeking
4. Request escalation explicitly

**We're Here to Help:**
Our goal is to resolve every issue fairly and quickly.`,
        keywords: ['escalate', 'manager', 'supervisor', 'complaint', 'not resolved']
      },
      {
        id: 'compliance',
        question: 'Where can I find compliance and legal disclosures?',
        answer: `**Legal & Compliance:**

**Policy Documents:**
- Available in your Document Center
- Includes full terms and conditions
- State-specific disclosures included

**Key Disclosures:**
- LossPay is underwritten by licensed insurers
- Coverage subject to policy terms
- State regulations may affect coverage
- Surplus lines disclosures (where applicable)

**Important Links:**
- Privacy Policy: losspay.com/privacy
- Terms of Service: losspay.com/terms
- State Notices: losspay.com/legal/state-notices

**Questions?**
Contact compliance@losspay.com for regulatory inquiries.`,
        keywords: ['compliance', 'legal', 'disclosure', 'regulation', 'terms'],
        cta: { label: 'View Documents', path: '/portal' }
      }
    ]
  },
  complaints: {
    title: 'Complaints & Your Rights',
    icon: AlertCircle,
    color: 'orange',
    description: 'How to file a complaint and your policyholder rights',
    questions: [
      {
        id: 'how-to-file-complaint',
        question: 'How do I file a complaint?',
        answer: `**Filing a Complaint:**

If you're not satisfied with our service, you have the right to file a complaint at any time.

**Step 1: Contact LossPay First**
We encourage you to contact us first. Our team is committed to resolving your concerns.

- **Phone:** 1-833-LOSSPAY (1-833-567-7729)
- **Email:** complaints@losspay.com
- **Mail:** LossPay, 16192 Coastal Highway, Lewes, DE 19958

We will acknowledge your complaint within 3 business days and provide a response within 15 business days.

**Step 2: Contact Your State Insurance Department**
If you're not satisfied with our response, you may file a complaint with your state's insurance regulator.

Your policy documents include state-specific contact information for filing complaints.

**Important:** Filing a complaint will not affect your policy or pending claims.`,
        keywords: ['complaint', 'file', 'dispute', 'unhappy', 'not satisfied', 'problem'],
        cta: { label: 'View Complaint Notice', path: '/complaint-notice' }
      },
      {
        id: 'policyholder-rights',
        question: 'What are my rights as a policyholder?',
        answer: `**Your Policyholder Rights:**

As a LossPay policyholder, you have the right to:

**Information Rights:**
- Receive clear information about your coverage
- Access your policy documents at any time
- Get explanations for claim decisions

**Complaint Rights:**
- File a complaint with LossPay at any time
- Escalate to your state insurance department
- Receive timely responses to your concerns

**Claim Rights:**
- Fair and timely claim processing
- Written explanation if a claim is denied
- Appeal a claim decision

**Privacy Rights:**
- Know how your information is used
- Access your personal data
- Opt out of marketing communications

**Cancellation Rights:**
- Cancel your policy at any time
- Receive prorated refund if eligible
- Written confirmation of cancellation`,
        keywords: ['rights', 'policyholder', 'entitled', 'what can I', 'fair']
      },
      {
        id: 'state-regulator-contact',
        question: 'How do I contact my state insurance department?',
        answer: `**Your State Insurance Department:**

Your policy is governed by your state's insurance regulations. To file a complaint with your state regulator:

1. **Check your Complaint Notice document** in the Document Center - it contains your state-specific contact information
2. **Or contact LossPay** and we can provide your state regulator's details

**What to Include in a State Complaint:**
- Your policy number
- Description of the issue
- Copies of relevant correspondence
- What resolution you're seeking

**Your complaint notice document contains:**
- Your state regulator's name
- Phone number
- Online complaint filing URL
- Mailing address

Filing a complaint with your state regulator will not affect your policy or pending claims.`,
        keywords: ['state', 'regulator', 'department', 'insurance', 'DOI', 'TDI', 'contact'],
        cta: { label: 'View Your Complaint Notice', path: '/complaint-notice' },
        isDynamic: true  // Flag for dynamic state-specific content
      },
      {
        id: 'complaint-response-time',
        question: 'How long until I receive a response to my complaint?',
        answer: `**Complaint Response Timeline:**

**From LossPay:**
| Stage | Timeline |
|-------|----------|
| Acknowledgment | Within 3 business days |
| Initial Response | Within 15 business days |
| Resolution | Depends on complexity |

**From State Regulators:**
Response times vary by state, but most departments:
- Acknowledge receipt within 5-10 business days
- Complete investigation within 30-60 days
- Provide written findings upon completion

**Expedited Review:**
If your complaint involves:
- Claim denial
- Policy cancellation
- Urgent financial hardship

Please indicate this when filing, as it may qualify for expedited review.

**Keep Records:**
Save all correspondence, including confirmation numbers and dates.`,
        keywords: ['response', 'time', 'how long', 'wait', 'complaint', 'timeline']
      },
      {
        id: 'complaint-wont-affect-policy',
        question: 'Will filing a complaint affect my policy?',
        answer: `**No. Filing a complaint will NOT affect your policy or claims.**

**Protected Rights:**
- Your policy cannot be cancelled for filing a complaint
- Your claim cannot be denied for filing a complaint
- Your rates cannot be increased for filing a complaint

**Legal Protections:**
State insurance laws protect consumers from retaliation. Insurance companies are prohibited from taking adverse action against policyholders who exercise their complaint rights.

**We Value Feedback:**
At LossPay, we view complaints as opportunities to improve. Your feedback helps us provide better service to all policyholders.

**Confidentiality:**
Your complaint information is kept confidential and used only to resolve your concerns.`,
        keywords: ['affect', 'retaliation', 'cancel', 'deny', 'safe', 'protected']
      }
    ]
  }
};

// Color mappings
const colorMap = {
  blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', iconBg: 'bg-blue-100' },
  green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200', iconBg: 'bg-green-100' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', iconBg: 'bg-purple-100' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', iconBg: 'bg-orange-100' },
  red: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', iconBg: 'bg-red-100' },
  teal: { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200', iconBg: 'bg-teal-100' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200', iconBg: 'bg-indigo-100' }
};

// Popular questions (most common)
const popularQuestions = [
  { category: 'claims', id: 'how-to-file-claim' },
  { category: 'claims', id: 'claim-deadline' },
  { category: 'billing', id: 'failed-payment' },
  { category: 'coverage', id: 'what-does-losspay-cover' },
  { category: 'cancellation', id: 'how-to-cancel' },
  { category: 'complaints', id: 'how-to-file-complaint' }
];

export default function HelpCenterPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || null);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // State-specific complaint guidance
  const { token } = useAuth();
  const [stateGuidance, setStateGuidance] = useState(null);
  const [loadingGuidance, setLoadingGuidance] = useState(false);
  const [userPolicyNumber, setUserPolicyNumber] = useState(null);

  // Fetch user's policy state and complaint guidance
  useEffect(() => {
    const fetchStateGuidance = async () => {
      if (!token) return;
      
      setLoadingGuidance(true);
      try {
        // First get user's policy to find their state
        const policyRes = await fetch(`${API_URL}/api/portal/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (policyRes.ok) {
          const policyData = await policyRes.json();
          // Dashboard returns primary_policy, not policy
          const policyNumber = policyData?.primary_policy?.policy_number;
          const stateCode = policyData?.primary_policy?.controlling_state || '';
          
          // Store policy number for navigation
          if (policyNumber) {
            setUserPolicyNumber(policyNumber);
          }
          
          if (stateCode) {
            // Fetch state-specific complaint guidance
            const guidanceRes = await fetch(`${API_URL}/api/complaint/compact/${stateCode}`);
            if (guidanceRes.ok) {
              const guidance = await guidanceRes.json();
              setStateGuidance(guidance);
            }
          }
        }
      } catch (err) {
        console.log('[HelpCenter] Could not fetch state guidance:', err);
      } finally {
        setLoadingGuidance(false);
      }
    };
    
    fetchStateGuidance();
  }, [token]);

  // Get category from URL on mount
  useEffect(() => {
    const category = searchParams.get('category');
    const questionId = searchParams.get('q');
    
    if (category && faqData[category]) {
      setActiveCategory(category);
      if (questionId) {
        setExpandedQuestions(new Set([questionId]));
        // Scroll to question after render
        setTimeout(() => {
          document.getElementById(questionId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [searchParams]);

  // Search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const results = [];
    const lowerQuery = query.toLowerCase();

    Object.entries(faqData).forEach(([categoryKey, category]) => {
      category.questions.forEach((q) => {
        const matchScore = 
          (q.question.toLowerCase().includes(lowerQuery) ? 3 : 0) +
          (q.keywords?.some(k => k.toLowerCase().includes(lowerQuery)) ? 2 : 0) +
          (q.answer.toLowerCase().includes(lowerQuery) ? 1 : 0);
        
        if (matchScore > 0) {
          results.push({
            ...q,
            categoryKey,
            categoryTitle: category.title,
            matchScore
          });
        }
      });
    });

    results.sort((a, b) => b.matchScore - a.matchScore);
    setSearchResults(results.slice(0, 8));
    setShowSearchResults(true);
  };

  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const selectCategory = (categoryKey) => {
    setActiveCategory(categoryKey);
    setSearchParams({ category: categoryKey });
    setShowSearchResults(false);
    setSearchQuery('');
    window.scrollTo(0, 0);
  };

  const goToQuestion = (categoryKey, questionId) => {
    setActiveCategory(categoryKey);
    setExpandedQuestions(new Set([questionId]));
    setSearchParams({ category: categoryKey, q: questionId });
    setShowSearchResults(false);
    setSearchQuery('');
    setTimeout(() => {
      document.getElementById(questionId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const renderMarkdown = (text) => {
    // Simple markdown rendering
    return text
      .split('\n')
      .map((line, i) => {
        // Bold
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Headers
        if (line.startsWith('# ')) {
          return <h3 key={i} className="text-lg font-bold mt-4 mb-2" dangerouslySetInnerHTML={{ __html: line.slice(2) }} />;
        }
        // List items
        if (line.startsWith('- ') || line.startsWith('• ')) {
          return <li key={i} className="ml-4" dangerouslySetInnerHTML={{ __html: line.slice(2) }} />;
        }
        // Checkmarks and X marks
        if (line.startsWith('✅ ') || line.startsWith('❌ ')) {
          return <p key={i} className="flex items-start gap-2 my-1" dangerouslySetInnerHTML={{ __html: line }} />;
        }
        // Table rows (simple)
        if (line.startsWith('|')) {
          return null; // Skip table markdown, handle separately
        }
        // Empty lines
        if (line.trim() === '' || line.trim() === '---') {
          return <div key={i} className="h-2" />;
        }
        return <p key={i} className="my-1" dangerouslySetInnerHTML={{ __html: line }} />;
      });
  };

  return (
    <PortalLayout pageTitle="Help Center" data-testid="help-center-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-0 pt-8 sm:pt-10">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-medium mb-3 sm:mb-4">
            <HelpCircle className="h-4 w-4" />
            Help Center
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-2 sm:mb-3 px-2 sm:px-0">
            How can we help you?
          </h1>
          <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto px-2 sm:px-0">
            Find answers to common questions about your coverage, claims, billing, and more.
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6 sm:mb-8">
          <div className="relative">
            <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
              className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-slate-200 rounded-xl sm:rounded-2xl focus:border-[#3B5BDB] focus:ring-0 focus:outline-none transition-colors"
              data-testid="help-search-input"
            />
          </div>

          {/* Search Results Dropdown */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 z-50 max-h-96 overflow-y-auto">
              {searchResults.map((result, idx) => (
                <button
                  key={`${result.categoryKey}-${result.id}`}
                  onClick={() => goToQuestion(result.categoryKey, result.id)}
                  className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-start gap-3"
                >
                  <div className={`w-8 h-8 rounded-lg ${colorMap[faqData[result.categoryKey].color].iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    {React.createElement(faqData[result.categoryKey].icon, { className: `h-4 w-4 ${colorMap[faqData[result.categoryKey].color].text}` })}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{result.question}</p>
                    <p className="text-sm text-slate-500">{result.categoryTitle}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {showSearchResults && searchQuery.length >= 2 && searchResults.length === 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 z-50 p-6 text-center">
              <p className="text-slate-600">No results found for "{searchQuery}"</p>
              <p className="text-sm text-slate-500 mt-1">Try different keywords or browse categories below</p>
            </div>
          )}
        </div>

        {/* Click outside to close search */}
        {showSearchResults && (
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowSearchResults(false)}
          />
        )}

        {!activeCategory ? (
          <>
            {/* Popular Questions */}
            <div className="mb-8 sm:mb-10">
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" />
                Popular Questions
              </h2>
              <div className="grid gap-2 sm:gap-3">
                {popularQuestions.map(({ category, id }) => {
                  const q = faqData[category].questions.find(q => q.id === id);
                  if (!q) return null;
                  return (
                    <button
                      key={id}
                      onClick={() => goToQuestion(category, id)}
                      className="flex items-center gap-3 p-3 sm:p-4 bg-white border border-slate-200 rounded-xl hover:border-[#3B5BDB] hover:shadow-md transition-all text-left group"
                    >
                      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg ${colorMap[faqData[category].color].iconBg} flex items-center justify-center flex-shrink-0`}>
                        {React.createElement(faqData[category].icon, { className: `h-4 w-4 sm:h-5 sm:w-5 ${colorMap[faqData[category].color].text}` })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm sm:text-base font-medium text-slate-900 group-hover:text-[#3B5BDB] transition-colors">{q.question}</p>
                        <p className="text-xs sm:text-sm text-slate-500">{faqData[category].title}</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-[#3B5BDB] transition-colors flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Category Grid */}
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4 flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-slate-600" />
              Browse by Topic
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-8 sm:mb-10">
              {Object.entries(faqData).map(([key, category]) => {
                const colors = colorMap[category.color];
                const Icon = category.icon;
                return (
                  <button
                    key={key}
                    onClick={() => selectCategory(key)}
                    className={`p-4 sm:p-5 rounded-xl sm:rounded-2xl border-2 ${colors.border} ${colors.bg} hover:shadow-lg transition-all text-left group`}
                    data-testid={`category-${key}`}
                  >
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${colors.iconBg} flex items-center justify-center mb-2 sm:mb-3`}>
                      <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${colors.text}`} />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-1 group-hover:text-[#3B5BDB] transition-colors">
                      {category.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600">{category.description}</p>
                    <p className="text-xs text-slate-400 mt-1 sm:mt-2">{category.questions.length} articles</p>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {/* Category View */}
            <button
              onClick={() => {
                setActiveCategory(null);
                setSearchParams({});
              }}
              className="flex items-center gap-2 text-[#3B5BDB] font-medium mb-6 hover:underline"
            >
              <ChevronRight className="h-4 w-4 rotate-180" />
              Back to all topics
            </button>

            {(() => {
              const category = faqData[activeCategory];
              const colors = colorMap[category.color];
              const Icon = category.icon;

              return (
                <div>
                  {/* Category Header */}
                  <div className={`p-6 rounded-2xl ${colors.bg} border ${colors.border} mb-6`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl ${colors.iconBg} flex items-center justify-center`}>
                        <Icon className={`h-7 w-7 ${colors.text}`} />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-slate-900">{category.title}</h2>
                        <p className="text-slate-600">{category.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* FAQ Accordions */}
                  <div className="space-y-3">
                    {category.questions.map((q) => {
                      const isExpanded = expandedQuestions.has(q.id);
                      return (
                        <div
                          key={q.id}
                          id={q.id}
                          className={`bg-white rounded-xl border-2 transition-all ${
                            isExpanded ? 'border-[#3B5BDB] shadow-lg' : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <button
                            onClick={() => toggleQuestion(q.id)}
                            className="w-full flex items-center justify-between p-5 text-left"
                            data-testid={`faq-${q.id}`}
                          >
                            <div className="flex items-center gap-3">
                              {q.priority && (
                                <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">
                                  IMPORTANT
                                </span>
                              )}
                              <span className="font-semibold text-slate-900">{q.question}</span>
                            </div>
                            <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>

                          {isExpanded && (
                            <div className="px-5 pb-5 border-t border-slate-100">
                              <div className="prose prose-slate max-w-none pt-4 text-slate-700">
                                {renderMarkdown(q.answer)}
                              </div>

                              {/* Dynamic State-Specific Complaint Guidance */}
                              {q.isDynamic && activeCategory === 'complaints' && (
                                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl" data-testid="state-complaint-guidance">
                                  {loadingGuidance ? (
                                    <div className="flex items-center gap-2 text-blue-600">
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                      <span className="text-sm">Loading your state information...</span>
                                    </div>
                                  ) : stateGuidance ? (
                                    <div>
                                      <h4 className="font-bold text-blue-900 mb-2">
                                        Your State: {stateGuidance.state_name}
                                      </h4>
                                      <div className="space-y-2 text-sm">
                                        <p className="font-medium text-blue-800">{stateGuidance.regulator_name}</p>
                                        {stateGuidance.regulator_phone && (
                                          <p className="text-blue-700">
                                            <strong>Phone:</strong> {stateGuidance.regulator_phone}
                                          </p>
                                        )}
                                        {stateGuidance.regulator_online_complaint_url && (
                                          <p className="text-blue-700">
                                            <strong>File Online:</strong>{' '}
                                            <a 
                                              href={stateGuidance.regulator_online_complaint_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-[#3B5BDB] hover:underline"
                                            >
                                              {stateGuidance.regulator_online_complaint_url}
                                            </a>
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-blue-700 text-sm">
                                      Your state-specific contact information is available in your Complaint Notice document in the Document Center.
                                    </p>
                                  )}
                                </div>
                              )}

                              {q.cta && (
                                <Button
                                  onClick={() => navigate(q.cta.path)}
                                  className="mt-4 bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl"
                                >
                                  {q.cta.label}
                                  <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Still Need Help */}
                  <Card className="mt-8 bg-slate-50 border-slate-200">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#3B5BDB] rounded-xl flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-slate-900 mb-1">Still need help?</h3>
                          <p className="text-slate-600 text-sm mb-4">
                            Can't find what you're looking for? Our support team is here to help.
                          </p>
                          <div className="flex flex-wrap gap-3">
                            <Button
                              variant="outline"
                              onClick={() => window.open('mailto:support@losspay.com', '_self')}
                              className="rounded-xl"
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Email Support
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => window.open('https://losspay.com/contact', '_blank')}
                              className="rounded-xl"
                            >
                              <Phone className="h-4 w-4 mr-2" />
                              Contact Us
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })()}
          </>
        )}

        {/* Disclaimer */}
        <div className="mt-10 mb-12 p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-500">
              <p className="font-medium text-slate-600 mb-1">Disclaimer</p>
              <p>
                This content is for informational purposes only and does not constitute legal or insurance advice. 
                Coverage is subject to the terms, conditions, and exclusions of your policy. 
                Please refer to your policy documents for complete details.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
